import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  Calendar, 
  Building2, 
  Users, 
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  BarChart3,
  PieChart,
  Activity,
  Filter,
  RotateCcw
} from 'lucide-react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  AreaChart,
  Area
} from 'recharts';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';

const COLORS_PIE = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6'];

const TRANSPORT_LABELS = {
  'EVE_NAKIL': 'Eve Nakil',
  'SAGLIK_TEDBIRI': 'Sağlık Tedbiri',
  'KONSULTASYON': 'Konsültasyon',
  'SERVIS_SEVKI': 'Servis Sevki',
  'EVE_NAKIL_EVDE_SAGLIK': 'Eve Nakil/Evde Sağlık',
  'HASTANEYE_SEVK_EVDE_SAGLIK': 'Hastaneye Sevk/Evde Sağlık',
  'DIYALIZ_SEVK': 'Diyaliz Sevk',
  'TIBBI_TETIK': 'Tıbbi Tetik',
  'VAKA_GOREV_IPTAL': 'Vaka/Görev İptal',
  'SOSYAL_ENDIKASYON': 'Sosyal Endikasyon',
  'NICU_SEVK': 'NICU Sevk',
  'KYB_SEVK': 'KYB Sevk',
  'GYB_SEVK': 'GYB Sevk',
  'NAKIL_RED': 'Nakil Red',
  'PICU_SEVK': 'PICU Sevk',
  'BAKIMEVI_SEVK': 'BAKIMEVİ Sevk',
  'RANDEVULU_VAKA': 'Randevulu Vaka',
  'ACIL_SEVK': 'Acil Sevk',
  'MR_GIDIS': 'MR Gidiş',
  'MR_DONUS': 'MR Dönüş'
};

const getTransportTypeLabel = (type) => TRANSPORT_LABELS[type] || type;

const CUSTOM_PIE_LABEL = ({ cx, cy, midAngle, innerRadius, outerRadius, value, percent, name }) => {
  if (value === 0) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="bold" fontSize={14}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const BAR_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316',
  '#84cc16', '#a855f7', '#0ea5e9', '#22c55e', '#e11d48',
  '#7c3aed', '#0891b2'
];

const CaseReportPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const debounceTimer = useRef(null);

  if (!['admin', 'super_admin', 'supervisor', 'external_presidency', 'external_hospital', 'external_parent_institution'].includes(user?.role)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">Erişim Reddedildi</h2>
            <p className="text-red-600">Bu sayfayı görüntülemek için admin yetkisine sahip olmalısınız.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const now = new Date();
  const todayTR = now.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' }); // YYYY-MM-DD in Turkey time
  const defaultFrom = todayTR.slice(0, 7) + '-01'; // First day of current month
  const defaultTo = todayTR;

  const [filters, setFilters] = useState({
    date_from: defaultFrom,
    date_to: defaultTo,
    institution_id: 'all',
    transport_type: 'all',
    assigned_team_id: 'all',
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 400);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [filters]);

  const { data: institutionsDataRaw } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => api.get('/institutions').then(res => res.data.data),
    staleTime: 5 * 60 * 1000,
  });

  // external_presidency sadece başkanlıkları görebilir
  // external_hospital kendi hastanesinin bağlı olduğu başkanlığı default olarak görür
  // external_parent_institution sadece alt kurumlarını görür
  const institutionsData = user?.role === 'external_presidency'
    ? (institutionsDataRaw || []).filter(inst => (inst.name || '').toUpperCase().includes('BAŞKANLIK'))
    : user?.role === 'external_hospital' && user?.hospital_institution_id
    ? (institutionsDataRaw || []).filter(inst => inst.id === user.hospital_institution_id)
    : user?.role === 'external_parent_institution' && user?.institution_id
    ? (institutionsDataRaw || []).filter(inst => inst.parent_institution_id === user.institution_id)
    : institutionsDataRaw;

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/teams').then(res => res.data.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: reportData, isLoading, isFetching, error } = useQuery({
    queryKey: ['caseReport', debouncedFilters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (debouncedFilters.date_from) params.append('date_from', debouncedFilters.date_from);
      if (debouncedFilters.date_to) params.append('date_to', debouncedFilters.date_to);
      if (debouncedFilters.institution_id !== 'all') params.append('institution_id', debouncedFilters.institution_id);
      if (debouncedFilters.transport_type !== 'all') params.append('transport_type', debouncedFilters.transport_type);
      if (debouncedFilters.assigned_team_id !== 'all') {
        params.append('assigned_team_id', debouncedFilters.assigned_team_id === 'unassigned' ? '0' : debouncedFilters.assigned_team_id);
      }
      // Geniş tarih aralıklarında rapor hesaplaması uzun sürebilir; genel 30 sn limitini aş
      return api.get(`/cases/report?${params.toString()}`, { timeout: 120000 }).then(res => res.data);
    },
    enabled: ['admin', 'super_admin', 'supervisor', 'external_presidency', 'external_hospital', 'external_parent_institution'].includes(user?.role),
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      date_from: defaultFrom,
      date_to: defaultTo,
      institution_id: 'all',
      transport_type: 'all',
      assigned_team_id: 'all',
    });
  }, [defaultFrom, defaultTo]);

  const kpis = reportData?.data?.kpis || {};
  const charts = reportData?.data?.charts || {};

  const pieData = useMemo(() => {
    const completed = charts.caseResults?.completed || 0;
    const cancelled = charts.caseResults?.cancelled || 0;
    const data = [];
    if (completed > 0) data.push({ name: 'Nakil Tamamlandı', value: completed });
    if (cancelled > 0) data.push({ name: 'Nakil İptal', value: cancelled });
    return data;
  }, [charts.caseResults]);

  const caseTypesData = useMemo(() => {
    if (!charts.caseTypes) return [];
    return charts.caseTypes.map((item, idx) => ({
      name: getTransportTypeLabel(item.type),
      value: item.count,
      fill: BAR_COLORS[idx % BAR_COLORS.length],
    }));
  }, [charts.caseTypes]);

  const hourlyData = useMemo(() => {
    if (!charts.hourlyBreakdown) return [];
    return charts.hourlyBreakdown;
  }, [charts.hourlyBreakdown]);

  const notificationHourlyData = useMemo(() => charts.notificationHourlyBreakdown || [], [charts.notificationHourlyBreakdown]);
  const arrivalHourlyData = useMemo(() => charts.arrivalHourlyBreakdown || [], [charts.arrivalHourlyBreakdown]);
  const deliveryHourlyData = useMemo(() => charts.deliveryHourlyBreakdown || [], [charts.deliveryHourlyBreakdown]);

  const presidencyBreakdownData = useMemo(() => {
    if (!charts.presidencyBreakdown) return [];
    return charts.presidencyBreakdown;
  }, [charts.presidencyBreakdown]);

  const isExternalPresidency = user?.role === 'external_presidency';
  const isExternalParentInstitution = user?.role === 'external_parent_institution';
  const showPresidencyBreakdown = isExternalPresidency && presidencyBreakdownData.length > 0 && filters.institution_id === 'all';

  const institutionBreakdownData = useMemo(() => {
    if (!charts.institutionBreakdown) return [];
    return charts.institutionBreakdown;
  }, [charts.institutionBreakdown]);
  const showInstitutionBreakdown = isExternalParentInstitution && institutionBreakdownData.length > 0 && filters.institution_id === 'all';


  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">Hata</h2>
            <p className="text-red-600">{error.message || 'Rapor yüklenirken hata oluştu'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Hizmet Raporu</h1>
              <p className="text-sm text-slate-500 mt-1">Vaka istatistikleri ve analizleri</p>
            </div>
            {isFetching && !isLoading && (
              <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                <div className="h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>Güncelleniyor...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4 shadow-md border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span>Filtreler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600">
                    {user?.role === 'external_presidency' ? 'Başkanlık' : 'Kurum'}
                  </Label>
                  <Select value={filters.institution_id} onValueChange={(v) => handleFilterChange('institution_id', v)}>
                    <SelectTrigger className="mt-1 text-sm">
                      <SelectValue placeholder={user?.role === 'external_presidency' ? 'Tüm Başkanlıklar' : 'Tümü'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {user?.role === 'external_presidency' ? 'Tüm Başkanlıklar' : 'Tümü'}
                      </SelectItem>
                      {institutionsData?.map((inst) => (
                        <SelectItem key={inst.id} value={inst.id.toString()}>{inst.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600">Sevk Türü</Label>
                  <Select value={filters.transport_type} onValueChange={(v) => handleFilterChange('transport_type', v)}>
                    <SelectTrigger className="mt-1 text-sm">
                      <SelectValue placeholder="Tümü" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {Object.entries(TRANSPORT_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-medium text-slate-600">Mobil Ekip</Label>
                  <Select value={filters.assigned_team_id} onValueChange={(v) => handleFilterChange('assigned_team_id', v)}>
                    <SelectTrigger className="mt-1 text-sm">
                      <SelectValue placeholder="Tümü" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="unassigned">Atanmamış</SelectItem>
                      {teamsData?.map((team) => (
                        <SelectItem key={team.id} value={team.id.toString()}>{team.code} - {team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={resetFilters} variant="outline" size="sm" className="w-full text-sm">
                  <RotateCcw className="h-3.5 w-3.5 mr-2" />
                  Filtreleri Temizle
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {isLoading && !reportData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-28 bg-white/60 rounded-xl animate-pulse" />
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-80 bg-white/60 rounded-xl animate-pulse" />
                  <div className="h-80 bg-white/60 rounded-xl animate-pulse" />
                </div>
              </div>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <KpiCard
                    icon={<FileText className="h-5 w-5" />}
                    iconBg="bg-blue-100"
                    iconColor="text-blue-600"
                    label="Toplam Vaka"
                    value={kpis.totalCases || 0}
                  />
                  <Card className="shadow-md border-slate-200 hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="pt-5 pb-4 px-5">
                      <div className="flex items-start justify-between">
                        <div className="space-y-3 flex-1">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Tamamlanan / İptal Edilen</p>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                              <div>
                                <p className="font-bold text-lg text-slate-900">{kpis.completedCases || 0}</p>
                                <p className="text-xs font-medium text-green-600">
                                  {kpis.totalCases > 0 ? `${((kpis.completedCases / kpis.totalCases) * 100).toFixed(1)}% tamamlandı` : '—'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                              <div>
                                <p className="font-bold text-lg text-slate-900">{kpis.cancelledCases || 0}</p>
                                <p className="text-xs font-medium text-red-600">
                                  {kpis.totalCases > 0 ? `${((kpis.cancelledCases / kpis.totalCases) * 100).toFixed(1)}% iptal` : '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="p-2.5 bg-slate-100 rounded-xl flex-shrink-0">
                          <Activity className="h-5 w-5 text-slate-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <KpiCard
                    icon={<Clock className="h-5 w-5" />}
                    iconBg="bg-orange-100"
                    iconColor="text-orange-600"
                    label="Ort. Ulaşım Süresi"
                    value={kpis.avgArrivalTime || '00:00:00'}
                    isTime
                  />
                  <KpiCard
                    icon={<Clock className="h-5 w-5" />}
                    iconBg="bg-amber-100"
                    iconColor="text-amber-600"
                    label="Ort. Operasyon Süresi"
                    value={kpis.avgOperationTime || '00:00:00'}
                    isTime
                  />
                  <KpiCard
                    icon={<TrendingUp className="h-5 w-5" />}
                    iconBg="bg-emerald-100"
                    iconColor="text-emerald-600"
                    label="Günlük Ortalama"
                    value={kpis.dailyAverage || 0}
                    subtitle="vaka/gün"
                  />
                  <KpiCard
                    icon={<BarChart3 className="h-5 w-5" />}
                    iconBg="bg-indigo-100"
                    iconColor="text-indigo-600"
                    label="Haftalık Ortalama"
                    value={kpis.weeklyAverage || 0}
                    subtitle="vaka/hafta"
                  />
                </div>

                {/* Presidency Breakdown Cards - only for external_presidency showing all */}
                {showPresidencyBreakdown && (
                  <Card className="shadow-md border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-teal-500" />
                        <span>Başkanlık Bazlı Vaka Dağılımı</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {presidencyBreakdownData.map((item, idx) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 bg-teal-50 rounded-xl border border-teal-100"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="h-9 w-9 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-700 leading-tight">{item.name || `Başkanlık #${item.id}`}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  <span className="text-green-600 font-medium">{item.completed}</span> tamamlandı &middot; <span className="text-red-500 font-medium">{item.cancelled}</span> iptal
                                </p>
                              </div>
                            </div>
                            <div className="text-right ml-2">
                              <p className="text-xl font-bold text-teal-700">{item.count}</p>
                              <p className="text-xs text-slate-400">vaka</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Institution Breakdown Cards - only for external_parent_institution showing all */}
                {showInstitutionBreakdown && (
                  <Card className="shadow-md border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-indigo-500" />
                        <span>Alt Kurum Bazlı Vaka Dağılımı</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {institutionBreakdownData.map((item, idx) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 bg-indigo-50 rounded-xl border border-indigo-100"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="h-9 w-9 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-700 leading-tight">{item.name || `Kurum #${item.id}`}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  <span className="text-green-600 font-medium">{item.completed}</span> tamamlandı &middot; <span className="text-red-500 font-medium">{item.cancelled}</span> iptal
                                </p>
                              </div>
                            </div>
                            <div className="text-right ml-2">
                              <p className="text-xl font-bold text-indigo-700">{item.count}</p>
                              <p className="text-xs text-slate-400">vaka</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <Card className="shadow-md border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <PieChart className="h-4 w-4 text-slate-500" />
                        <span>Vaka Sonuçları</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pieData.length > 0 ? (
                        <div>
                          <ResponsiveContainer width="100%" height={280}>
                            <RechartsPieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={110}
                                paddingAngle={2}
                                dataKey="value"
                                label={CUSTOM_PIE_LABEL}
                                labelLine={false}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell
                                    key={idx}
                                    fill={entry.name === 'Nakil Tamamlandı' ? '#22c55e' : '#ef4444'}
                                    stroke="white"
                                    strokeWidth={3}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value, name) => [`${value} vaka`, name]}
                                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                              />
                              <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                iconSize={10}
                                formatter={(value) => <span className="text-sm text-slate-700">{value}</span>}
                              />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                          <div className="grid grid-cols-2 gap-3 mt-2">
                            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl border border-green-100">
                              <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center">
                                <CheckCircle className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-green-700">{charts.caseResults?.completed || 0}</p>
                                <p className="text-xs text-green-600">Tamamlandı</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-xl border border-red-100">
                              <div className="h-10 w-10 rounded-full bg-red-500 flex items-center justify-center">
                                <XCircle className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-red-700">{charts.caseResults?.cancelled || 0}</p>
                                <p className="text-xs text-red-600">İptal</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <EmptyState />
                      )}
                    </CardContent>
                  </Card>

                  {/* Case Types Bar Chart */}
                  <Card className="shadow-md border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center space-x-2">
                        <BarChart3 className="h-4 w-4 text-slate-500" />
                        <span>Vaka Çeşitleri</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {caseTypesData.length > 0 ? (
                        <div>
                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                              data={caseTypesData}
                              layout="vertical"
                              margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                              <YAxis
                                dataKey="name"
                                type="category"
                                width={120}
                                tick={{ fontSize: 11 }}
                                stroke="#94a3b8"
                              />
                              <Tooltip
                                formatter={(value) => [`${value} vaka`]}
                                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                              />
                              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                                {caseTypesData.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.fill} />
                                ))}
                                <LabelList dataKey="value" position="right" style={{ fontSize: 11, fontWeight: 600, fill: '#475569' }} />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <EmptyState />
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Hourly Breakdown Charts - stacked */}
                <HourlyBreakdownChart
                  title="Vaka Kırılımları (Çağrı Saati)"
                  data={hourlyData}
                  gradientId="hourlyGradientCall"
                  color="#0d9488"
                  gradientColor="#14b8a6"
                />
                <HourlyBreakdownChart
                  title="Vaka Kırılımları (Ekibe Bildirim Saati)"
                  data={notificationHourlyData}
                  gradientId="hourlyGradientNotification"
                  color="#7c3aed"
                  gradientColor="#8b5cf6"
                />
                <HourlyBreakdownChart
                  title="Vaka Kırılımları (Vakaya Ulaşma Saati)"
                  data={arrivalHourlyData}
                  gradientId="hourlyGradientArrival"
                  color="#2563eb"
                  gradientColor="#3b82f6"
                />
                <HourlyBreakdownChart
                  title="Vaka Kırılımları (Vaka Teslim Saati)"
                  data={deliveryHourlyData}
                  gradientId="hourlyGradientDelivery"
                  color="#ea580c"
                  gradientColor="#f97316"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon, iconBg, iconColor, label, value, subtitle, subtitleColor, isTime }) => (
  <Card className="shadow-md border-slate-200 hover:shadow-lg transition-shadow duration-200">
    <CardContent className="pt-5 pb-4 px-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`font-bold ${isTime ? 'text-xl' : 'text-2xl'} text-slate-900`}>{value}</p>
          {subtitle && (
            <p className={`text-xs font-medium ${subtitleColor || 'text-slate-400'}`}>{subtitle}</p>
          )}
        </div>
        <div className={`p-2.5 ${iconBg} rounded-xl`}>
          <div className={iconColor}>{icon}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const HourlyBreakdownChart = ({ title, data, gradientId, color, gradientColor }) => (
  <Card className="shadow-md border-slate-200">
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center space-x-2">
        <Activity className="h-4 w-4 text-slate-500" />
        <span>{title}</span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      {data.length > 0 && data.some(d => d.count > 0) ? (
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={data}
            margin={{ top: 25, right: 20, left: 0, bottom: 5 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={gradientColor} stopOpacity={0.6} />
                <stop offset="95%" stopColor={gradientColor} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="hourLabel"
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(value) => [`${value} vaka`, 'Vaka Sayısı']}
              labelFormatter={(label) => `Saat: ${label}`}
              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={color}
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={{ r: 4, fill: '#fff', stroke: color, strokeWidth: 2 }}
              activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
            >
              <LabelList
                dataKey="count"
                position="top"
                offset={10}
                style={{ fontSize: 11, fontWeight: 700, fill: '#334155' }}
              />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState />
      )}
    </CardContent>
  </Card>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
    <BarChart3 className="h-10 w-10 mb-2" />
    <p className="text-sm">Veri bulunamadı</p>
  </div>
);

export default CaseReportPage;
